// src/components/annotation/strategies/OBBStrategy.ts
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import {
  GeometryStrategy,
  InteractionContext,
  ResizeHandleName,
  KeyModifiers,
} from './GeometryStrategy'
import {
  getMinMaxFromPoints,
  getPointDistance,
  isPointInOBB,
  pointsArrayToStructured,
  getBoundingBoxCenter,
  rotatePoint,
} from '../../../utils/helpers'

type EdgeBounds = { left: number; top: number; right: number; bottom: number }

function ensurePoints(box: AnnotationDataBoundingBox): number[] {
  if (!box.points || box.points.length !== 8) {
    // fallback to axis-aligned from min/max
    const xmin = box.xmin ?? 0
    const ymin = box.ymin ?? 0
    const xmax = box.xmax ?? xmin
    const ymax = box.ymax ?? ymin
    return [xmin, ymin, xmax, ymin, xmax, ymax, xmin, ymax]
  }
  return [...box.points]
}

export const obbStrategy: GeometryStrategy = {
  hitTest: (point, box) => {
    const pts = ensurePoints(box)
    return isPointInOBB(point, pts)
  },

  getActiveHandle: (box, mouseX, mouseY, ctx: InteractionContext) => {
    const pts = ensurePoints(box)
    const { imageWidth, imageHeight, canvasWidth, canvasHeight, handleSizeCanvas } = ctx

    // Map to canvas space to keep parity with existing handle detection
    const scaleX = canvasWidth / imageWidth
    const scaleY = canvasHeight / imageHeight
    const canvasPoints = [
      pts[0] * scaleX,
      pts[1] * scaleY,
      pts[2] * scaleX,
      pts[3] * scaleY,
      pts[4] * scaleX,
      pts[5] * scaleY,
      pts[6] * scaleX,
      pts[7] * scaleY,
    ]

    const bbox = pointsArrayToStructured(canvasPoints)
    // Corner handles: place slightly inside along both inward normals to match draw logic
    const inwardOffset = handleSizeCanvas * 0.6
    const inward = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number) => {
      // From corner A, vectors to adjacent corners B and C
      const v1x = bx - ax
      const v1y = by - ay
      const v2x = cx - ax
      const v2y = cy - ay
      // inward approx as normalized sum of inward directions
      const len1 = Math.hypot(v1x, v1y) || 1
      const len2 = Math.hypot(v2x, v2y) || 1
      const nx = v1x / len1 + v2x / len2
      const ny = v1y / len1 + v2y / len2
      const nlen = Math.hypot(nx, ny) || 1
      return { x: ax + (nx / nlen) * inwardOffset, y: ay + (ny / nlen) * inwardOffset }
    }
    const tlIn = inward(
      bbox.topLeft.x,
      bbox.topLeft.y,
      bbox.topRight.x,
      bbox.topRight.y,
      bbox.bottomLeft.x,
      bbox.bottomLeft.y
    )
    const trIn = inward(
      bbox.topRight.x,
      bbox.topRight.y,
      bbox.bottomRight.x,
      bbox.bottomRight.y,
      bbox.topLeft.x,
      bbox.topLeft.y
    )
    const brIn = inward(
      bbox.bottomRight.x,
      bbox.bottomRight.y,
      bbox.bottomLeft.x,
      bbox.bottomLeft.y,
      bbox.topRight.x,
      bbox.topRight.y
    )
    const blIn = inward(
      bbox.bottomLeft.x,
      bbox.bottomLeft.y,
      bbox.topLeft.x,
      bbox.topLeft.y,
      bbox.bottomRight.x,
      bbox.bottomRight.y
    )

    const handles: Array<{
      name: ResizeHandleName
      x: number
      y: number
      kind: 'corner' | 'edge' | 'rotate'
    }> = [
      { name: 'topLeft', x: tlIn.x, y: tlIn.y, kind: 'corner' },
      { name: 'topRight', x: trIn.x, y: trIn.y, kind: 'corner' },
      { name: 'bottomRight', x: brIn.x, y: brIn.y, kind: 'corner' },
      { name: 'bottomLeft', x: blIn.x, y: blIn.y, kind: 'corner' },
    ]

    // Edge handles: compute mid-segment points and threshold length
    // Derive a local handle size based on box size to reduce overlap and match visuals
    const minX = Math.min(bbox.topLeft.x, bbox.topRight.x, bbox.bottomRight.x, bbox.bottomLeft.x)
    const maxX = Math.max(bbox.topLeft.x, bbox.topRight.x, bbox.bottomRight.x, bbox.bottomLeft.x)
    const minY = Math.min(bbox.topLeft.y, bbox.topRight.y, bbox.bottomRight.y, bbox.bottomLeft.y)
    const maxY = Math.max(bbox.topLeft.y, bbox.topRight.y, bbox.bottomRight.y, bbox.bottomLeft.y)
    const widthCanvas = Math.max(1, maxX - minX)
    const heightCanvas = Math.max(1, maxY - minY)
    const minSideCanvas = Math.min(widthCanvas, heightCanvas)
    const HANDLE_RATIO = 0.06
    const handleSizeLocal = Math.max(4, minSideCanvas * HANDLE_RATIO)
    const hitPaddingLocal = Math.max(2, handleSizeLocal * 0.25)
    const edgeLengthMin = Math.max(8, 10 / ctx.zoomLevel)
    const edgeFraction = 0.12
    const edgeBounds: Record<string, EdgeBounds> = {}
    const addEdgeHandle = (
      name: ResizeHandleName,
      ax: number,
      ay: number,
      bx: number,
      by: number
    ) => {
      const length = getPointDistance({ x: ax, y: ay }, { x: bx, y: by })
      if (length === 0) return
      const seg = Math.max(edgeLengthMin, length * edgeFraction)
      // center segment around mid point
      const mx = (ax + bx) / 2
      const my = (ay + by) / 2
      const ux = (bx - ax) / length
      const uy = (by - ay) / length
      const nx = -uy
      const ny = ux
      const halfLen = seg / 2
      const halfThick = Math.max(2, handleSizeLocal * 0.5)
      // Compute 4 corners of oriented rectangle and then AABB
      const p1x = mx - ux * halfLen - nx * halfThick
      const p1y = my - uy * halfLen - ny * halfThick
      const p2x = mx + ux * halfLen - nx * halfThick
      const p2y = my + uy * halfLen - ny * halfThick
      const p3x = mx + ux * halfLen + nx * halfThick
      const p3y = my + uy * halfLen + ny * halfThick
      const p4x = mx - ux * halfLen + nx * halfThick
      const p4y = my - uy * halfLen + ny * halfThick
      const minX = Math.min(p1x, p2x, p3x, p4x)
      const maxX = Math.max(p1x, p2x, p3x, p4x)
      const minY = Math.min(p1y, p2y, p3y, p4y)
      const maxY = Math.max(p1y, p2y, p3y, p4y)
      handles.push({ name, x: mx, y: my, kind: 'edge' })
      edgeBounds[name] = { left: minX, top: minY, right: maxX, bottom: maxY }
    }

    addEdgeHandle('top', bbox.topLeft.x, bbox.topLeft.y, bbox.topRight.x, bbox.topRight.y)
    addEdgeHandle('right', bbox.topRight.x, bbox.topRight.y, bbox.bottomRight.x, bbox.bottomRight.y)
    addEdgeHandle(
      'bottom',
      bbox.bottomLeft.x,
      bbox.bottomLeft.y,
      bbox.bottomRight.x,
      bbox.bottomRight.y
    )
    addEdgeHandle('left', bbox.topLeft.x, bbox.topLeft.y, bbox.bottomLeft.x, bbox.bottomLeft.y)

    // Add a rotate handle: midpoint of the top edge, offset inward along its normal
    {
      const ax = bbox.topLeft.x
      const ay = bbox.topLeft.y
      const bx = bbox.topRight.x
      const by = bbox.topRight.y
      const mx = (ax + bx) / 2
      const my = (ay + by) / 2
      const dx = bx - ax
      const dy = by - ay
      const len = Math.hypot(dx, dy) || 1
      // Normal from top edge; choose direction toward bbox center (inward)
      let nx = -dy / len
      let ny = dx / len
      const cx = (bbox.topLeft.x + bbox.topRight.x + bbox.bottomRight.x + bbox.bottomLeft.x) / 4
      const cy = (bbox.topLeft.y + bbox.topRight.y + bbox.bottomRight.y + bbox.bottomLeft.y) / 4
      const dot = (mx - cx) * nx + (my - cy) * ny
      // Flip to ensure inward
      if (dot > 0) {
        nx = -nx
        ny = -ny
      }
      // Position just inside the edge; approximate visual offset using handle size
      const rotateOffset = handleSizeLocal * 1.1
      const rx = mx + nx * rotateOffset
      const ry = my + ny * rotateOffset
      handles.push({ name: 'rotate', x: rx, y: ry, kind: 'rotate' })
    }

    // Convert mouse to canvas space
    const canvasMouseX = mouseX * scaleX
    const canvasMouseY = mouseY * scaleY
    // Collect all hits and pick the nearest to reduce overlap ambiguity
    const hits: Array<{
      name: ResizeHandleName
      x: number
      y: number
      kind: 'corner' | 'edge' | 'rotate'
    }> = []
    for (const h of handles) {
      if (h.kind === 'edge') {
        const bounds = edgeBounds[h.name]
        if (bounds) {
          const pad = hitPaddingLocal * 0.3
          if (
            canvasMouseX >= bounds.left - pad &&
            canvasMouseX <= bounds.right + pad &&
            canvasMouseY >= bounds.top - pad &&
            canvasMouseY <= bounds.bottom + pad
          ) {
            hits.push(h)
          }
        }
      } else {
        const half = handleSizeLocal / 2
        const rect = {
          left: h.x - half - hitPaddingLocal,
          right: h.x + half + hitPaddingLocal,
          top: h.y - half - hitPaddingLocal,
          bottom: h.y + half + hitPaddingLocal,
        }
        if (
          canvasMouseX >= rect.left &&
          canvasMouseX <= rect.right &&
          canvasMouseY >= rect.top &&
          canvasMouseY <= rect.bottom
        ) {
          hits.push(h)
        }
      }
    }
    if (hits.length === 0) return null
    // Prefer non-edge (corner/rotate) if distances are comparable
    const squaredDist = (hx: number, hy: number) => {
      const dx = hx - canvasMouseX
      const dy = hy - canvasMouseY
      return dx * dx + dy * dy
    }
    let best = hits[0]
    let bestDist = squaredDist(best.x, best.y)
    for (let i = 1; i < hits.length; i++) {
      const h = hits[i]
      const d = squaredDist(h.x, h.y)
      const prefer = h.kind !== 'edge' && best.kind === 'edge'
      if (d < bestDist - 1e-3 || prefer) {
        best = h
        bestDist = d
      }
    }
    return best.name
  },

  resize: (box, handle, mouseX, mouseY, options) => {
    const updated: AnnotationDataBoundingBox = { ...box }
    const pts = ensurePoints(updated)
    const c = pointsArrayToStructured(pts)
    const u = { x: c.topRight.x - c.topLeft.x, y: c.topRight.y - c.topLeft.y }
    const v = { x: c.bottomLeft.x - c.topLeft.x, y: c.bottomLeft.y - c.topLeft.y }
    const uLen = Math.hypot(u.x, u.y) || 1
    const vLen = Math.hypot(v.x, v.y) || 1
    const uDir = { x: u.x / uLen, y: u.y / uLen }
    const vDir = { x: v.x / vLen, y: v.y / vLen }

    const clampLen = (len: number) => Math.max(1, len)

    const compose = (O: { x: number; y: number }, lenU: number, lenV: number) => {
      const U = { x: uDir.x * lenU, y: uDir.y * lenU }
      const V = { x: vDir.x * lenV, y: vDir.y * lenV }
      const TL = { x: O.x, y: O.y }
      const TR = { x: O.x + U.x, y: O.y + U.y }
      const BL = { x: O.x + V.x, y: O.y + V.y }
      const BR = { x: O.x + U.x + V.x, y: O.y + U.y + V.y }
      const arr = [TL.x, TL.y, TR.x, TR.y, BR.x, BR.y, BL.x, BL.y]
      updated.points = arr
      const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(arr)
      updated.xmin = xmin
      updated.ymin = ymin
      updated.xmax = xmax
      updated.ymax = ymax
      return updated
    }

    switch (handle) {
      case 'rotate': {
        // Compute rotation angle delta around a pivot (center by default)
        const pivot = options?.interaction?.pivot || getBoundingBoxCenter(pts)
        const startPts = options?.interaction?.startPoints || pts
        const s = pointsArrayToStructured(startPts)
        // Reference vector at drag start: midpoint of the top edge relative to pivot
        const startMidTop = {
          x: (s.topLeft.x + s.topRight.x) / 2,
          y: (s.topLeft.y + s.topRight.y) / 2,
        }
        const vStart = { x: startMidTop.x - pivot.x, y: startMidTop.y - pivot.y }
        const startAngle = Math.atan2(vStart.y, vStart.x)

        // Current vector from pivot to mouse
        const vCurr = { x: mouseX - pivot.x, y: mouseY - pivot.y }
        const currAngle = Math.atan2(vCurr.y, vCurr.x)
        // Use shortest angular difference to avoid sudden flips when crossing ±π
        let angleDelta = Math.atan2(
          Math.sin(currAngle - startAngle),
          Math.cos(currAngle - startAngle)
        )

        // Modifiers: Shift to snap to 15°, Alt for fine control (reduce sensitivity)
        const mods = options?.modifiers || { shift: false, ctrl: false, alt: false }
        if (mods.shift) {
          const snap = (15 * Math.PI) / 180
          angleDelta = Math.round(angleDelta / snap) * snap
        }

        const arr: number[] = []
        for (let i = 0; i < 8; i += 2) {
          // Rotate original start points, not incrementally mutated points
          const rp = rotatePoint({ x: startPts[i], y: startPts[i + 1] }, pivot, angleDelta)
          arr.push(rp.x, rp.y)
        }
        updated.points = arr
        const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(arr)
        updated.xmin = xmin
        updated.ymin = ymin
        updated.xmax = xmax
        updated.ymax = ymax
        return updated
      }
      case 'topLeft': {
        const A = c.bottomRight
        const dx = A.x - mouseX
        const dy = A.y - mouseY
        const lenU = clampLen(dx * uDir.x + dy * uDir.y)
        const lenV = clampLen(dx * vDir.x + dy * vDir.y)
        const O = { x: A.x - uDir.x * lenU - vDir.x * lenV, y: A.y - uDir.y * lenU - vDir.y * lenV }
        return compose(O, lenU, lenV)
      }
      case 'topRight': {
        const A = c.bottomLeft
        const dx = mouseX - A.x
        const dy = mouseY - A.y
        const lenU = clampLen(dx * uDir.x + dy * uDir.y)
        const lenV = clampLen(-(dx * vDir.x + dy * vDir.y))
        const O = { x: A.x - vDir.x * lenV, y: A.y - vDir.y * lenV }
        return compose(O, lenU, lenV)
      }
      case 'bottomRight': {
        const A = c.topLeft
        const dx = mouseX - A.x
        const dy = mouseY - A.y
        const lenU = clampLen(dx * uDir.x + dy * uDir.y)
        const lenV = clampLen(dx * vDir.x + dy * vDir.y)
        const O = { x: A.x, y: A.y }
        return compose(O, lenU, lenV)
      }
      case 'bottomLeft': {
        const A = c.topRight
        const dx = mouseX - A.x
        const dy = mouseY - A.y
        const lenU = clampLen(-(dx * uDir.x + dy * uDir.y))
        const lenV = clampLen(dx * vDir.x + dy * vDir.y)
        const O = { x: A.x - uDir.x * lenU, y: A.y - uDir.y * lenU }
        return compose(O, lenU, lenV)
      }
      case 'top': {
        const A = c.bottomRight
        const dx = A.x - mouseX
        const dy = A.y - mouseY
        const lenV = clampLen(dx * vDir.x + dy * vDir.y)
        const O = { x: A.x - uDir.x * uLen - vDir.x * lenV, y: A.y - uDir.y * uLen - vDir.y * lenV }
        return compose(O, uLen, lenV)
      }
      case 'bottom': {
        const A = c.topRight
        const dx = mouseX - A.x
        const dy = mouseY - A.y
        const lenV = clampLen(dx * vDir.x + dy * vDir.y)
        const O = { x: c.topLeft.x, y: c.topLeft.y }
        return compose(O, uLen, lenV)
      }
      case 'left': {
        const A = c.bottomRight
        const dx = A.x - mouseX
        const dy = A.y - mouseY
        const lenU = clampLen(dx * uDir.x + dy * uDir.y)
        const O = { x: A.x - vDir.x * vLen - uDir.x * lenU, y: A.y - vDir.y * vLen - uDir.y * lenU }
        return compose(O, lenU, vLen)
      }
      case 'right': {
        const A = c.topLeft
        const dx = mouseX - A.x
        const dy = mouseY - A.y
        const lenU = clampLen(dx * uDir.x + dy * uDir.y)
        const O = { x: A.x, y: A.y }
        return compose(O, lenU, vLen)
      }
    }

    return updated
  },

  normalizeAfterMouseUp: (box) => box,

  onKeyDown: (box, key: string, modifiers: KeyModifiers, _ctx: InteractionContext) => {
    const updated: AnnotationDataBoundingBox = { ...box }
    const pts = ensurePoints(updated)
    const center = getBoundingBoxCenter(pts)

    const moveStepBase = 1
    const moveStep = modifiers.shift ? moveStepBase * 5 : moveStepBase
    const rotateStep = (modifiers.shift ? 5 : 1) * (Math.PI / 180) // degrees to rad
    const scaleStep = modifiers.shift ? 0.05 : 0.02

    const applyPoints = (p: number[]) => {
      updated.points = p
      const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(p)
      updated.xmin = xmin
      updated.ymin = ymin
      updated.xmax = xmax
      updated.ymax = ymax
    }

    const rotate = (sign: 1 | -1) => {
      const angle = sign * rotateStep
      const arr: number[] = []
      for (let i = 0; i < 8; i += 2) {
        const rp = rotatePoint({ x: pts[i], y: pts[i + 1] }, center, angle)
        arr.push(rp.x, rp.y)
      }
      applyPoints(arr)
    }

    const move = (dx: number, dy: number) => {
      const arr: number[] = []
      for (let i = 0; i < 8; i += 2) arr.push(pts[i] + dx, pts[i + 1] + dy)
      applyPoints(arr)
    }

    const scaleLocal = (which: 'u' | 'v', sign: 1 | -1) => {
      const factor = 1 + sign * scaleStep
      // Use the current local edge axes (not forced orthonormal), and solve coefficients in oblique basis
      const cstruct = pointsArrayToStructured(pts)
      const uvec = {
        x: cstruct.topRight.x - cstruct.topLeft.x,
        y: cstruct.topRight.y - cstruct.topLeft.y,
      }
      const vvec = {
        x: cstruct.bottomLeft.x - cstruct.topLeft.x,
        y: cstruct.bottomLeft.y - cstruct.topLeft.y,
      }
      // Normalize to stabilize, but keep oblique relationship
      const ulen = Math.hypot(uvec.x, uvec.y) || 1
      const vlen = Math.hypot(vvec.x, vvec.y) || 1
      const ux = uvec.x / ulen
      const uy = uvec.y / ulen
      const vx = vvec.x / vlen
      const vy = vvec.y / vlen
      const det = ux * vy - uy * vx || 1 // non-zero for non-collinear axes
      const arr: number[] = []
      for (let i = 0; i < 8; i += 2) {
        const dx = pts[i] - center.x
        const dy = pts[i + 1] - center.y
        // Solve [ux vx; uy vy] [au; av] = [dx; dy]
        const au = (dx * vy - dy * vx) / det
        const av = (-dx * uy + dy * ux) / det
        const scu = which === 'u' ? factor : 1
        const scv = which === 'v' ? factor : 1
        const nx = center.x + au * scu * ux + av * scv * vx
        const ny = center.y + au * scu * uy + av * scv * vy
        arr.push(nx, ny)
      }
      applyPoints(arr)
    }

    switch (key) {
      // Movement
      case 'ArrowUp':
        if (modifiers.alt) scaleLocal('v', 1)
        else if (modifiers.ctrl) rotate(-1)
        else move(0, -moveStep)
        return updated
      case 'ArrowDown':
        if (modifiers.alt) scaleLocal('v', -1)
        else if (modifiers.ctrl) rotate(1)
        else move(0, moveStep)
        return updated
      case 'ArrowLeft':
        if (modifiers.alt) scaleLocal('u', -1)
        else if (modifiers.ctrl) rotate(-1)
        else move(-moveStep, 0)
        return updated
      case 'ArrowRight':
        if (modifiers.alt) scaleLocal('u', 1)
        else if (modifiers.ctrl) rotate(1)
        else move(moveStep, 0)
        return updated
      default:
        return null
    }
  },
}
